/**
 * Workspace shell for the org- and user-scoped resource areas (GDN-2723).
 *
 * One layout serves both levels because the level *is* the route: a `userId`
 * segment means user scope, its absence means org scope. That's the whole
 * point of moving resources under `/organization/{id}/…` — before this, the
 * level was implicit and every page had to reconstruct it (or guess, via
 * `navigate(-1)`).
 *
 * The rail and context stack mirror the entity pages they hang off, so moving
 * between an org's Resource Explorer and its other sections doesn't feel like
 * leaving the org.
 */

import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { useGetProviderInfoQuery, useGetV2AdminOrganizationsByOrganizationIdQuery } from '../store/hooks';
import { parseResourceScope, scopeHomePath } from '../utils/resource-routes';
import { AppHeader } from './app-header';
import { LoadingState } from './loading-state';
import { ORG_NAV_GROUPS, orgRailDestination } from './org-nav';
import { WorkspaceSidebar } from './org-page-redesign/WorkspaceSidebar';
import { buildProviderNavGroups } from './provider-nav';

/** Rail keys that own a resource subpage rather than an entity-page section. */
const USER_SUBPAGE_KEYS = new Set([
  'encounters',
  'appointments',
  'transcriptions',
  'notes',
  'resources',
]);

export function ResourceWorkspaceLayout() {
  const params = useParams<{ organizationId?: string; userId?: string }>();
  const navigate = useNavigate();
  const scope = parseResourceScope(params);

  const { data: organization, isLoading: orgLoading } =
    useGetV2AdminOrganizationsByOrganizationIdQuery(
      { organizationId: scope?.organizationId ?? '' },
      { skip: !scope },
    );

  const { data: provider, isLoading: providerLoading } = useGetProviderInfoQuery(
    { providerId: scope?.userId ?? '' },
    { skip: !scope?.userId },
  );

  if (!scope) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-2xl font-bold text-gray-900">
            Missing organization
          </h2>
          <p className="text-gray-600 mt-2">
            Resources are always org-scoped, so this URL has nothing to show.
          </p>
        </main>
      </div>
    );
  }

  if (orgLoading || providerLoading) {
    return <LoadingState message="Loading workspace..." />;
  }

  const orgPath = scopeHomePath({ organizationId: scope.organizationId });
  const isUserLevel = Boolean(scope.userId);

  // Rail selection lands on the owning entity page's section — the rail
  // behaves identically here and there.
  const goToSection = (key: string) => {
    if (isUserLevel) {
      const userPath = scopeHomePath(scope);
      if (USER_SUBPAGE_KEYS.has(key)) {
        navigate(`${userPath}/${key}`);
        return;
      }
      navigate(`${userPath}?section=${encodeURIComponent(key)}`);
      return;
    }
    const linkTarget = orgRailDestination(key, scope.organizationId);
    if (linkTarget) {
      navigate(linkTarget);
      return;
    }
    navigate(`${orgPath}?tab=${encodeURIComponent(key)}`);
  };

  return (
    <div className="flex h-full overflow-hidden bg-gray-50">
      <WorkspaceSidebar
        groups={
          isUserLevel
            ? buildProviderNavGroups(true, undefined, undefined)
            : ORG_NAV_GROUPS
        }
        activeKey={isUserLevel ? 'resources' : 'resource-explorer'}
        onSelect={goToSection}
        contextStack={[
          { label: 'Admin v2', onSelect: () => navigate('/admin-v2') },
          {
            label: organization?.name || 'Organization',
            ...(isUserLevel ? {} : { chip: 'Org' }),
            onSelect: () => navigate(orgPath),
          },
          ...(isUserLevel
            ? [
                {
                  label: provider?.name || 'User',
                  chip: 'User',
                  onSelect: () => navigate(scopeHomePath(scope)),
                },
              ]
            : []),
        ]}
      />

      <div className="min-w-0 flex-1 overflow-y-auto">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
